/**
 * The cell — the unit `TableData` is made of, now that it can hold a span.
 *
 * Shaped to match `CellData` in `TableBlock.tsx` so the bridge passes cells
 * through instead of converting them. The spec wrote this field as `text`; the
 * block's persisted shape calls it `content`, and matching the block is the
 * point of the exercise, so `content` wins and `text` is accepted on the way in.
 *
 * A span is stored on its ANCHOR cell only. The cells it covers are not present
 * in the row at all — a 2-column span in a 4-column table means that row holds
 * three cells, not four. Every function here is written against that rule,
 * because the alternative (placeholder cells) means two representations of the
 * same table and a class of bug where they disagree.
 */

export interface Cell {
  content: string;
  /** Columns this cell occupies, including its own. Absent or 1 = ordinary. */
  colspan?: number;
  /** Rows this cell occupies, including its own. */
  rowspan?: number;
  /** A note printed under the table, with a mark in this cell (2g, `tableLayout.ts`). */
  note?: string;
}

/** Anything that has ever been persisted as a cell. */
export type LooseCell = string | { content?: string; text?: string; colspan?: number; rowspan?: number; note?: string } | null | undefined;

export const cellText = (c: LooseCell): string => {
  if (typeof c === "string") return c;
  if (c && typeof c === "object") return c.content ?? c.text ?? "";
  return "";
};

export const cellColspan = (c: LooseCell): number =>
  typeof c === "object" && c ? Math.max(1, Math.floor(c.colspan ?? 1)) : 1;

export const cellRowspan = (c: LooseCell): number =>
  typeof c === "object" && c ? Math.max(1, Math.floor(c.rowspan ?? 1)) : 1;

export const isSpanned = (c: LooseCell): boolean => cellColspan(c) > 1 || cellRowspan(c) > 1;

/** Normalise on the way in. Drops span:1 so plain cells stay plain in JSON. */
export function toCell(c: LooseCell): Cell {
  const out: Cell = { content: cellText(c) };
  const cs = cellColspan(c);
  const rs = cellRowspan(c);
  if (cs > 1) out.colspan = cs;
  if (rs > 1) out.rowspan = rs;
  // A note travels with its cell; an empty one is not kept.
  const note = typeof c === "object" && c && typeof c.note === "string" ? c.note.trim() : "";
  if (note) out.note = note;
  return out;
}

export const toCells = (row: readonly LooseCell[] | undefined): Cell[] => (row ?? []).map(toCell);

export const plain = (s: string): Cell => ({ content: s });

/**
 * How many columns a row actually occupies, spans included.
 *
 * This is the number that has to equal the table's width — a short row is a
 * short row whether it is short because cells are missing or because a span was
 * miscounted, and the author sees the same broken table either way.
 */
export const rowWidth = (row: readonly LooseCell[]): number =>
  row.reduce<number>((n, c) => n + cellColspan(c), 0);

/**
 * The column each cell in a row starts at, accounting for cells that rows above
 * are still occupying via `rowspan`.
 *
 * `occupied` carries, per column, how many further rows are covered from above.
 * Callers walk rows in order and pass the same array back in, which is how a
 * continuation row knows to skip the columns a `\multirow` above it owns.
 */
export function placeRow(
  row: readonly LooseCell[],
  occupied: number[],
): { starts: number[]; occupied: number[] } {
  const next = occupied.slice();
  const starts: number[] = [];
  let col = 0;
  for (const cell of row) {
    while (next[col] > 0) col++;            // a row above still owns this column
    starts.push(col);
    const cs = cellColspan(cell);
    const rs = cellRowspan(cell);
    for (let k = 0; k < cs; k++) {
      // -1 because this row is consuming one of them now.
      if (rs > 1) next[col + k] = rs - 1;
    }
    col += cs;
  }
  // Every column a previous row owned has had one of its rows consumed.
  for (let i = 0; i < next.length; i++) {
    if (occupied[i] > 0 && next[i] === occupied[i]) next[i] = occupied[i] - 1;
  }
  return { starts, occupied: next };
}

/** Total columns the table occupies, taking the widest placed row. */
export function tableWidth(header: readonly LooseCell[], rows: readonly (readonly LooseCell[])[], hasHeader: boolean): number {
  let occupied: number[] = [];
  let width = 0;
  const walk = (row: readonly LooseCell[]) => {
    const placed = placeRow(row, occupied);
    occupied = placed.occupied;
    const end = placed.starts.length
      ? placed.starts[placed.starts.length - 1] + cellColspan(row[row.length - 1])
      : 0;
    width = Math.max(width, end, rowWidth(row));
  };
  if (hasHeader) walk(header);
  rows.forEach(walk);
  return Math.max(1, width);
}
